import { StageController } from './StageController'
import {
  BoxGeometry,
  BufferGeometry,
  CanvasTexture,
  Color,
  ImageBitmapLoader,
  InstancedMesh,
  IUniform,
  ShaderMaterial,
  Texture,
  Vector2,
} from 'three'
import vertexShader from './moebius.vert'
import fragmentShader from './moebius.frag'
import { Easing, Group, Tween } from '@tweenjs/tween.js'
import { SpringVector2 } from '../lib/SpringVector2'
import { Spring } from '../lib/Spring'
import { Colors } from '../const'

export type MoebiusConfig = {
  fade: number
  speed: number
  spin: number
  tilt: number
  pivot: number
  scale: number
  position: Vector2
  rotation: Vector2
  image: string
}

const Count = 47
const SpringConfig = [50]

export class Moebius {
  private readonly group = new Group()
  private readonly textures: Record<string, Texture> = {}

  public config = {
    speed: new Spring(0, ...SpringConfig),
    fade: new Spring(0, ...SpringConfig),
    spin: new Spring(0, ...SpringConfig),
    tilt: new Spring(0, ...SpringConfig),
    pivot: new Spring(0, ...SpringConfig),
    scale: new Spring(0, ...SpringConfig),
    position: new SpringVector2(new Vector2(), ...SpringConfig),
    rotation: new SpringVector2(new Vector2(), ...SpringConfig),
  }

  public readonly mesh: InstancedMesh<BufferGeometry, ShaderMaterial>
  public readonly uniforms: {
    uScale: IUniform<number>
    uThickness: IUniform<number>
    uBlend: IUniform<number>
    uGrain: IUniform<number>
    uRadius: IUniform<number>
    uTilt: IUniform<number>
    uTwist: IUniform<number>
    uLoops: IUniform<number>
    uSpeed: IUniform<number>
    uPivot: IUniform<number>
    uFade: IUniform<number>
    uCount: IUniform<number>
    uFog: IUniform<number>
    uEdge: IUniform<number>
    uEdgeAlpha: IUniform<number>
    uElapsed: IUniform<number>
    uSpin: IUniform<number>
    uPosition: IUniform<Vector2>
    uRotation: IUniform<Vector2>
    uFogColor: IUniform<Color>
    uMap: IUniform<Texture | null>
    uBlendMap: IUniform<Texture | null>
  }
  private loader = new ImageBitmapLoader()

  constructor(private readonly stage: StageController) {
    this.uniforms = {
      uCount: { value: Count },
      uRadius: { value: 1 },
      uTwist: { value: 1 },
      uLoops: { value: 1 },
      uPivot: { value: 0 },
      uFog: { value: 0 },
      uThickness: { value: 0.35 },
      uEdge: { value: 0.25 },
      uEdgeAlpha: { value: 0.2 },
      uGrain: { value: 0.035 * this.stage.renderer.getPixelRatio() },
      //
      uSpeed: { value: 1 },
      uFade: { value: 1 },
      uSpin: { value: 0 },
      uTilt: { value: 0 },
      uScale: { value: 1 },
      uPosition: { value: new Vector2() },
      uRotation: { value: new Vector2() },
      //
      uBlend: { value: 0 },
      uElapsed: { value: 0 },
      uMap: { value: null },
      uBlendMap: { value: null },
      uFogColor: { value: new Color(Colors.grey).convertLinearToSRGB() },
    }

    const geometry = new BoxGeometry(0.5, 0.5, 0.5)
    geometry.rotateY(Math.PI * 0.5)

    const material = new ShaderMaterial({
      wireframe: false,
      uniforms: this.uniforms,
      transparent: true,
      vertexShader,
      fragmentShader,
    })

    this.mesh = new InstancedMesh(geometry, material, Count)
    this.stage.scene.add(this.mesh)
  }

  async load() {
    //TODO load initial config texture
  }

  show() {
    new Tween({ elapsed: 0 }, this.group)
      .to({ elapsed: 1 }, 5000)
      .easing(Easing.Cubic.InOut)
      .onUpdate(({ elapsed }) => (this.uniforms.uElapsed.value = elapsed))
      .start()
  }

  setTexture(texture: Texture) {
    texture.anisotropy = 4
    this.uniforms.uMap.value?.dispose()
    this.uniforms.uMap.value = texture
  }

  async loadTexture(src: string) {
    if (!this.textures[src]) {
      const imageBitmap = await this.loader.loadAsync(src)
      this.textures[src] = new CanvasTexture(imageBitmap)
      this.textures[src].anisotropy = 4
      this.stage.renderer.initTexture(this.textures[src])
      this.textures[src].needsUpdate = true
    }
    this.uniforms.uBlendMap.value = this.textures[src]

    new Tween({ blend: 0 }, this.group)
      .to({ blend: 1 }, 250)
      .onUpdate(({ blend }) => (this.uniforms.uBlend.value = blend))
      .onComplete(() => {
        this.uniforms.uMap.value = this.uniforms.uBlendMap.value
        this.uniforms.uBlend.value = 0
      })
      .start()
  }

  setConfig(config: MoebiusConfig, immediate = false) {
    const { position, rotation, fade, scale, speed, tilt, spin, image } = config

    this.config.fade.setTarget(fade, immediate)
    this.config.scale.setTarget(scale, immediate)
    this.config.tilt.setTarget(tilt, immediate)
    this.config.speed.setTarget(speed, immediate)
    this.config.spin.setTarget(spin, immediate)
    this.config.position.setFromVector2(position, immediate)
    this.config.rotation.setFromVector2(rotation, immediate)

    this.loadTexture(image)
  }

  dispose() {
    this.uniforms.uMap.value?.dispose()
    this.mesh.geometry.dispose()
    this.mesh.material.dispose()
    this.stage.scene.remove(this.mesh)
  }

  update(time: number, delta: number) {
    this.group.update(time)
    const { speed, fade, spin, tilt, pivot, scale, position, rotation } = this.config

    speed.update(delta)
    fade.update(delta)
    spin.update(delta)
    tilt.update(delta)
    scale.update(delta)
    position.update(delta)
    rotation.update(delta)

    this.uniforms.uSpeed.value += delta * speed.value * 0.1
    this.uniforms.uSpeed.value = this.uniforms.uSpeed.value % (Math.PI * 2)

    this.uniforms.uFade.value += delta * fade.value * 3
    this.uniforms.uFade.value = this.uniforms.uFade.value % Count

    this.uniforms.uSpin.value += delta * spin.value * 0.05
    this.uniforms.uSpin.value = this.uniforms.uSpin.value % (Math.PI * 2)

    this.uniforms.uTilt.value = tilt.value
    this.uniforms.uPivot.value = pivot.value
    this.uniforms.uScale.value = scale.value
    this.uniforms.uPosition.value.copy(position.vector)
    this.uniforms.uRotation.value.copy(rotation.vector)
  }
}
